import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';
import Papa from 'papaparse';
import html2canvas from 'html2canvas';
import TopicSelector from './TopicSelector';
import GradeSelector from './GradeSelector';
import SubjectSelector from './SubjectSelector';
import VerbGameSelector from './VerbGameSelector';
import VerbsRouter from './VerbsRouter';
import VerbGameWhoDidIt from './VerbGameWhoDidIt';
import VerbAnalysisGame from './VerbAnalysisGame';
import VerbTenseGame from './VerbTenseGame';
import VerbTenseGame2 from './VerbTenseGame2';
import VerbTenseGame3 from './VerbTenseGame3';
import ParticiplesGame, { PARTICIPLES_GAME_CONFIGS } from './ParticiplesGame';
import ParticiplesGameII from './ParticiplesGameII';
import ParticiplesGameIII from './ParticiplesGameIII';
import ImperativeMatchingGame from './ImperativeMatchingGame';
import ImperativeMatchingGameII from './ImperativeMatchingGameII';
import ImperativeMatchingGameIII from './ImperativeMatchingGameIII';
import VerbTenseGame1 from './VerbTenseGame1';
import VocabGame2 from './VocabGame2';
import VerbAnalysisGameExtended from './VerbAnalysisGameExtended';
import VerbAnalysisGameComplete from './VerbAnalysisGameComplete';
import MasdarBalloonGameBinyan4 from './MasdarBalloonGameBinyan4';
import JuniorHighSubjects from './JuniorHighSubjects';
import JuniorHighSpoken from './JuniorHighSpoken';
import JuniorHighVerbs from './JuniorHighVerbs';
import JuniorHighGrammarGames from './JuniorHighGrammarGames';
import JuniorHighGrammarGameRoute from './JuniorHighGrammarGameRoute';
import { findTopicById } from './utils/topics';
import VocabularyResumeDialog from './VocabularyResumeDialog';
import { completeVocabularyStage, getUnlockedVocabularyStage, getVocabularyReviewMistakes, loadVocabularyProgress, saveVocabularyProgress } from './utils/vocabularyProgress';
import './App.css';

function GameApp({ gradeFromUrl }) {
  const { grade: gradeFromParams, topicId: topicIdFromParams } = useParams();
  const actualGrade = gradeFromUrl || gradeFromParams;
  
  // מפצל תא שמכיל כמה חלופות (למקרה שכתבת "אופציה1 / אופציה2")
  const splitAlternatives = (cell) =>
    (cell || '')
      .split(/[\/|;،،]|،/g) // מפרידים נפוצים: / | ; פסיקים ערביים
      .map(s => s.trim())
      .filter(Boolean);

  // פונקציה להסרת ניקוד ערבי
  const removeArabicNiqqud = (str) => (str || '').replace(/[\u064B-\u065F]/g, '');

  // נרמול תשובת המשתמש/התרגום (בעברית):
  //  - בלי ניקוד
  //  - בלי מרכאות/גרשיים
  //  - בלי רווחים מיותרים
  //  - מאחד מקפים שונים
  const normalizeHebrew = (s) =>
    (s || '')
      .normalize('NFKC')
      .replace(/[\u0591-\u05C7]/g, '')         // מסיר ניקוד עברי
      .replace(/[״"׳']/g, '')                  // מסיר גרשיים/מרכאות
      .replace(/[‐-–—־]/g, '-')                // מאחד סוגי מקפים
      .replace(/\s+/g, ' ')                    // רווחים מרובים -> רווח אחד
      .trim();

  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const getVocabStagePlan = (totalWords) => (
    totalWords === 18
      ? { stage1Size: 9, stage2Size: 9, stage3Size: 9, stage4Size: 8 }
      : { stage1Size: 10, stage2Size: 10, stage3Size: 10, stage4Size: 8 }
  );

  // בשלב 4:
  // - תשובה שמסתיימת ב-"..." יכולה להתקבל גם בלי נקודות,
  //   עם כל כמות של נקודות, או עם מקף אחד בדיוק בסוף.
  // - תשובה שמסתיימת במילה "את" יכולה להתקבל גם עם 2-5 נקודות בסוף.
  const isAcceptedGame4Answer = (userAnswer, expectedAnswers) => {
    const normalizedUser = normalizeHebrew(userAnswer);

    return expectedAnswers.some((expectedAnswer) => {
      const normalizedExpected = normalizeHebrew(expectedAnswer);
      if (!normalizedExpected) return false;

      if (normalizedUser === normalizedExpected) {
        return true;
      }

      if (/\?$/.test(normalizedExpected)) {
        const questionMarkPattern = new RegExp(
          `^${escapeRegExp(normalizedExpected.replace(/\?$/u, '').trimEnd())}\\s+\\?$`,
          'u'
        );

        if (questionMarkPattern.test(normalizedUser)) {
          return true;
        }
      }

      if (/(?:^|\s)את$/u.test(normalizedExpected)) {
        const directObjectWithDotsPattern = new RegExp(
          `^${escapeRegExp(normalizedExpected)}\\s*(?:\\.{2,5}|…)$`,
          'u'
        );

        if (directObjectWithDotsPattern.test(normalizedUser)) {
          return true;
        }
      }

      if (!/\s*(?:\.\.\.|…)\s*$/u.test(normalizedExpected)) {
        return false;
      }

      const baseExpected = normalizedExpected
        .replace(/\s*(?:\.\.\.|…)\s*$/u, '')
        .trimEnd();

      if (!baseExpected) return false;

      const flexibleEndingPattern = new RegExp(
        `^${escapeRegExp(baseExpected)}(?:\\s*(?:\\.+|…+|-))?$`,
        'u'
      );

      return flexibleEndingPattern.test(normalizedUser);
    });
  };

  // State for grade selection
  const [selectedGrade, setSelectedGrade] = useState(actualGrade || null);
  const [gradeName, setGradeName] = useState('');

  // State for topic selection
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicName, setTopicName] = useState('');

  // State for storing word pairs loaded from CSV
  const [allWordPairs, setAllWordPairs] = useState([]);
  const [loadedTopic, setLoadedTopic] = useState(null);
  const [savedProgress, setSavedProgress] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [stageInitialized, setStageInitialized] = useState(false);
  const [stageSession, setStageSession] = useState(0);
  const completedSessionRef = useRef(null);

  const vocabStagePlan = getVocabStagePlan(allWordPairs.length);
  const { stage1Size, stage2Size, stage3Size, stage4Size } = vocabStagePlan;

  // Split words into stage-specific groups; 18-row lists use 9+9 instead of 10+10
  const gameSet1 = allWordPairs.slice(0, stage1Size);
  const gameSet2 = allWordPairs.slice(stage1Size, stage1Size + stage2Size);

  // State for current game level (1, 2, 3, or 4)
  const [currentGameLevel, setCurrentGameLevel] = useState(1);

  // State for game 3 words (to prevent them from changing during drag)
  const [game3Words, setGame3Words] = useState([]);

  // State for game 4 words
  const [game4Words, setGame4Words] = useState([]);

  // Mistakes in the active run; completed runs are kept separately in savedProgress.
  const [incorrectWords, setIncorrectWords] = useState([]);

  // State for tracking incorrect words specifically from game 4
  const [game4IncorrectWords, setGame4IncorrectWords] = useState([]);

  // State for game 4 text input mode
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isGame4InputFocused, setIsGame4InputFocused] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [checkedAnswers, setCheckedAnswers] = useState({}); // Track which answers were already checked correctly
  const [game4Attempts, setGame4Attempts] = useState({}); // Track attempts for each word in game 4
  const [game4IncorrectAnswers, setGame4IncorrectAnswers] = useState({}); // Track last incorrect answer for each word
  const [lastCheckedAnswers, setLastCheckedAnswers] = useState({}); // Track the last answer that was checked for each word

  // Get current word pairs based on game level
  const getWordPairs = () => {
    if (currentGameLevel === 1) return gameSet1;
    if (currentGameLevel === 2) return gameSet2;
    if (currentGameLevel === 3) return game3Words;
    // Game 4: Use the remaining words not used in game 3
    return game4Words;
  };

  const wordPairs = getWordPairs();

  // Create shuffled order of word indices for random Hebrew word sequence
  const [shuffledWordOrder, setShuffledWordOrder] = useState(() => {
    const indices = Array.from({ length: wordPairs.length }, (_, i) => i);
    return indices.sort(() => Math.random() - 0.5);
  });
  const [resultsWordOrder, setResultsWordOrder] = useState([]);

  // State for current target word and game progress
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
  const [draggedCard, setDraggedCard] = useState(null);

  // State for tracking attempts per word
  const [wordAttempts, setWordAttempts] = useState(() => {
    const initialAttempts = {};
    wordPairs.forEach((_, index) => {
      initialAttempts[index] = 0;
    });
    return initialAttempts;
  });
  const wordAttemptsRef = useRef(wordAttempts);
  const dragEndHandledRef = useRef(false);
  const dropProcessingRef = useRef(false);
  const activeDragPointerIdRef = useRef(null);
  
  // State for tracking words where user requested to see correct answer
  const [wordsShownCorrectAnswer, setWordsShownCorrectAnswer] = useState({});

  const isDragVocabularyStage = currentGameLevel === 1 || currentGameLevel === 3;

  const recordWordAttempt = (targetIndex) => {
    const nextAttempts = {
      ...wordAttemptsRef.current,
      [targetIndex]: (wordAttemptsRef.current[targetIndex] || 0) + 1
    };
    wordAttemptsRef.current = nextAttempts;
    setWordAttempts(nextAttempts);
    return nextAttempts[targetIndex];
  };

  const getFirstUnattemptedWordIndex = (attempts = wordAttemptsRef.current) => {
    if (!isDragVocabularyStage) return -1;
    return wordPairs.findIndex((_, index) => (attempts[index] || 0) === 0);
  };

  const getShuffledPositionForWordIndex = (wordIndex) => {
    const position = shuffledWordOrder.findIndex(index => index === wordIndex);
    return position >= 0 ? position : 0;
  };

  const getResultsWordOrder = () => {
    const fallbackOrder = wordPairs.map((_, index) => index);
    const isValidSavedOrder =
      resultsWordOrder.length === wordPairs.length &&
      resultsWordOrder.every(index => Number.isInteger(index) && index >= 0 && index < wordPairs.length);

    if (isValidSavedOrder) return resultsWordOrder;

    if (currentGameLevel === 1 || currentGameLevel === 3) {
      const isValidShuffledOrder =
        shuffledWordOrder.length === wordPairs.length &&
        shuffledWordOrder.every(index => Number.isInteger(index) && index >= 0 && index < wordPairs.length);

      if (isValidShuffledOrder) return shuffledWordOrder;
    }

    return fallbackOrder;
  };

  const returnToFirstUnattemptedWord = (attempts = wordAttemptsRef.current) => {
    const firstUnattemptedWordIndex = getFirstUnattemptedWordIndex(attempts);
    if (firstUnattemptedWordIndex === -1) return false;

    setCurrentTargetIndex(getShuffledPositionForWordIndex(firstUnattemptedWordIndex));
    return true;
  };

  const advanceAfterAnsweredDragWord = (targetIndex) => {
    if ((wordAttemptsRef.current[targetIndex] || 0) === 0) return false;

    const nextTargetIndex = currentTargetIndex + 1;
    if (nextTargetIndex >= shuffledWordOrder.length) {
      const returnedToMissingWord = returnToFirstUnattemptedWord();
      if (returnedToMissingWord) return false;

      setCurrentTargetIndex(nextTargetIndex);
      return true;
    }

    setCurrentTargetIndex(nextTargetIndex);
    return false;
  };

  useEffect(() => {
    wordAttemptsRef.current = wordAttempts;
  }, [wordAttempts]);

  // Shuffle the cards for a new game
  const shuffleCards = () => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
  };

  // State for tracking pointer drag
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState(null);

  // Handle pointer start (works for both mouse and touch)
  const handlePointerStart = (e, card) => {
    if (showFireworks || dropProcessingRef.current || activeDragPointerIdRef.current !== null) return;

    e.preventDefault();
    activeDragPointerIdRef.current = e.pointerId;
    dragEndHandledRef.current = false;
    setDraggedCard(card);
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDraggedElement(e.currentTarget);
    
    // Capture pointer for this element
    e.currentTarget.setPointerCapture(e.pointerId);
    
    // Bring to front
    e.currentTarget.style.zIndex = '1000';
  };

  // Handle pointer move
  const handlePointerMove = (e) => {
    if (e.pointerId !== activeDragPointerIdRef.current || !isDragging || !draggedElement) return;
    
    const dx = e.clientX - dragStartPos.x;
    const dy = e.clientY - dragStartPos.y;
    
    draggedElement.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const resetPointerDrag = () => {
    if (draggedElement) {
      draggedElement.style.transform = '';
      draggedElement.style.zIndex = '';
    }
    activeDragPointerIdRef.current = null;
    setIsDragging(false);
    setDraggedCard(null);
    setDraggedElement(null);
  };

  // A browser-cancelled gesture must never be counted as an answer.
  const handlePointerCancel = (e) => {
    if (activeDragPointerIdRef.current === null || e.pointerId !== activeDragPointerIdRef.current) return;
    dragEndHandledRef.current = true;
    resetPointerDrag();
  };

  // Handle pointer end
  const handlePointerEnd = (e) => {
    if (e.pointerId !== activeDragPointerIdRef.current || dragEndHandledRef.current || !isDragging || !draggedElement) return;
    dragEndHandledRef.current = true;
    
    // Reset transform and z-index
    draggedElement.style.transform = '';
    draggedElement.style.zIndex = '';
    
    // Check if we dropped on an instruction box
    const instructionBoxes = Array.from(document.querySelectorAll('.instruction-box'));
    
    for (const box of instructionBoxes) {
      const rect = box.getBoundingClientRect();
      const margin = 20;
      
      if (e.clientX >= rect.left - margin &&
          e.clientX <= rect.right + margin &&
          e.clientY >= rect.top - margin &&
          e.clientY <= rect.bottom + margin) {
        
        // Simulate drop event
        const dropEvent = new Event('drop');
        handleDrop(dropEvent, shuffledWordOrder[currentTargetIndex]);
        break;
      }
    }
    
    resetPointerDrag();
  };

  // State for fireworks animation
  const [showFireworks, setShowFireworks] = useState(false);

  // State for balloons animation
  const [showBalloons, setShowBalloons] = useState(false);

  // State for incorrect card animation
  const [incorrectCardId, setIncorrectCardId] = useState(null);
  
  // State for error flash on target word
  const [showErrorFlash, setShowErrorFlash] = useState(false);

  // State for results popup
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // Ref for results summary to capture as image
  const resultsSummaryRef = useRef(null);
  
  // Create a single AudioContext for all sounds (mobile optimization)
  const audioContextRef = useRef(null);
  
  // Initialize/resume AudioContext on the user's click/tap before playing sounds
  const initAudioContext = async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return null;

      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContextClass();
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      return audioContextRef.current.state === 'running' ? audioContextRef.current : null;
    } catch (error) {
      console.warn('AudioContext could not be initialized:', error);
      return null;
    }
  };
  
  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play success sound
  const playSuccessSound = async () => {
    const audioContext = await initAudioContext();
    if (!audioContext) return;

    // Create a simple success sound using Web Audio API
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
    oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
    oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  // Play error sound
  const playErrorSound = async () => {
    const audioContext = await initAudioContext();
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(200, audioContext.currentTime);

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  // Handle drop event
  const handleDrop = (e, targetIndex) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent processing if no card is being dragged or if already processing
    if (dropProcessingRef.current || !draggedCard || showFireworks) return;
    dropProcessingRef.current = true;

    const currentTarget = wordPairs[targetIndex];
    const currentCard = draggedCard;

    if (!currentTarget) {
      dropProcessingRef.current = false;
      return;
    }

    // Clear dragged card immediately to prevent double processing
    setDraggedCard(null);

    // Check match based on game level
    let isMatch;
    if (currentGameLevel === 3) {
      // For game 3: Hebrew is shown, user selects Arabic card
      // Accept any Arabic word that has the same Hebrew translation
      const targetHebrew = currentTarget.hebrew;
      const selectedArabic = currentCard.word;
      
      // Find if there's any word pair with this Arabic-Hebrew combination
      // Remove niqqud from both Arabic words for comparison
      isMatch = allWordPairs.some(pair => 
        removeArabicNiqqud(pair.arabic) === removeArabicNiqqud(selectedArabic) && 
        pair.hebrew === targetHebrew
      );
    } else {
      // For game 1: Arabic is shown, user selects Hebrew card
      isMatch = currentCard.word === currentTarget.hebrew;
    }

    if (isMatch) {
      // Increment attempts for current word
      recordWordAttempt(targetIndex);

      // Play success sound
      playSuccessSound();

      // Check if this is the last card
      const isLastCard = currentTargetIndex === shuffledWordOrder.length - 1;

      // Immediately hide the matched card
      setCards(prevCards => prevCards.map(card => 
        card.id === currentCard.id 
          ? { ...card, matched: true }
          : card
      ));

      if (isLastCard) {
        const finishedStage = advanceAfterAnsweredDragWord(targetIndex);

        if (finishedStage) {
          // Show balloons animation
          setShowBalloons(true);

          // Hide balloons after 4 seconds
          setTimeout(() => {
            setShowBalloons(false);
          }, 4000);
        }

        dropProcessingRef.current = false;
      } else {
        // Show fireworks animation for non-last cards
        setShowFireworks(true);

        // Wait 2 seconds before updating the game state
        setTimeout(() => {
          // Move to the next target word
          const finishedStage = advanceAfterAnsweredDragWord(targetIndex);
          if (finishedStage) {
            setShowBalloons(true);
            setTimeout(() => {
              setShowBalloons(false);
            }, 4000);
          }
          // Hide fireworks
          setShowFireworks(false);
          dropProcessingRef.current = false;
        }, 2000);
      }
    } else {
      // Increment attempts for current word (incorrect attempt)
      const attemptNumber = recordWordAttempt(targetIndex);

      // Track incorrect word for game 4 (only on first incorrect attempt)
      if (attemptNumber === 1) {
        const incorrectWord = wordPairs[targetIndex];
        setIncorrectWords(prev => [...prev, incorrectWord]);
        console.log('DEBUG - Added incorrect word:', incorrectWord);
      }

      // Play error sound
      playErrorSound();
      
      // Show error flash on target word
      setShowErrorFlash(true);
      
      // Handle incorrect drop - animate card return
      setIncorrectCardId(currentCard.id);

      // Reset the animations after 300ms (duration of error sound)
      setTimeout(() => {
        setIncorrectCardId(null);
        setShowErrorFlash(false);
        dropProcessingRef.current = false;
      }, 300);
    }
  };

  // Initialize cards state with shuffled order
  const [cards, setCards] = useState(
    wordPairs.map((pair, index) => ({
      id: index + 1,
      word: pair.hebrew, // Display Hebrew word on cards
      translation: pair.arabic, // Store Arabic translation for future use
      revealed: true, // Cards start revealed
      matched: false // Cards start unmatched
    }))
  );

  // Effect to load CSV data when topic is selected
  useEffect(() => {
    if (!selectedTopic) return;
    let cancelled = false;
    setLoadedTopic(null);
    setStageInitialized(false);

    const loadCSVData = async () => {
      try {
        const basePath = process.env.PUBLIC_URL || '';
        const csvUrl = `${basePath}/${selectedTopic}`.replace(/([^:]\/)\/+/g, '$1');
        const response = await fetch(csvUrl);
        const csvText = await response.text();

        Papa.parse(csvText, {
          complete: (results) => {
            if (cancelled) return;
            const wordPairs = results.data
              .filter(row => row.length >= 2 && row[0] && row[1]) // לפחות ערבית+עברית
              .map(row => {
                const arabic = String(row[0]).trim();
                const hebrew = String(row[1]).trim();
                // אוספים חלופות מעמודות C..F (אם קיימות), כולל פיצול בתוך תא אחד
                const alts = row.slice(2, 6) // C, D, E, F
                  .flatMap(cell => splitAlternatives(String(cell || '')))
                  .filter(Boolean);
                return { arabic, hebrew, alts };
              });
            const progress = loadVocabularyProgress(selectedGrade, selectedTopicId || selectedTopic, wordPairs);
            setSavedProgress(progress);
            setIncorrectWords([]);
            setGame3Words(progress?.game3Words || []);
            setGame4Words(progress?.game4Words || []);
            setGame4IncorrectWords(progress?.game4IncorrectWords || []);
            setShowResumeDialog(Boolean(progress));
            setAllWordPairs(wordPairs);
            setLoadedTopic(selectedTopic);
          },
          skipEmptyLines: true,
          encoding: 'UTF-8'
        });
      } catch (error) {
        if (cancelled) return;
        console.error('שגיאה בטעינת קובץ CSV:', error);
        // Fallback to empty array if CSV loading fails
        setAllWordPairs([]);
      }
    };

    loadCSVData();
    return () => { cancelled = true; };
  }, [selectedTopic, selectedGrade, selectedTopicId]);

  // Function to generate game 4 words based on incorrect words
  const generateGame4Words = () => {
    const uniqueIncorrectWords = getVocabularyReviewMistakes(savedProgress);

    let selectedWords = [];

    if (uniqueIncorrectWords.length >= stage4Size) {
      // If we have enough incorrect words, randomly select the stage quota from them
      const shuffledIncorrect = [...uniqueIncorrectWords].sort(() => Math.random() - 0.5);
      selectedWords = shuffledIncorrect.slice(0, stage4Size);
    } else {
      // If we have fewer incorrect words than the quota, use all of them and fill the rest
      selectedWords = [...uniqueIncorrectWords];

      // Get words that were NOT used in the last game 4 to fill the remaining slots
      const lastGame4WordIds = game4Words.map(word => `${word.arabic}-${word.hebrew}`);
      const availableWords = allWordPairs.filter(word => 
        !lastGame4WordIds.includes(`${word.arabic}-${word.hebrew}`) &&
        !uniqueIncorrectWords.some(incorrect => 
          incorrect.arabic === word.arabic && incorrect.hebrew === word.hebrew
        )
      );

      const neededWords = stage4Size - uniqueIncorrectWords.length;
      const shuffledAvailable = [...availableWords].sort(() => Math.random() - 0.5);
      selectedWords = [...selectedWords, ...shuffledAvailable.slice(0, neededWords)];
    }

    console.log('DEBUG - Selected words for game 4:', selectedWords);

    // Shuffle the final list so incorrect words aren't always first
    return selectedWords.sort(() => Math.random() - 0.5);
  };

  // Only a newly loaded topic starts automatically; later starts use the same stage initializer.
  useEffect(() => {
    if (loadedTopic !== selectedTopic || !allWordPairs.length || showResumeDialog) return;
    initializeVocabularyStage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWordPairs, loadedTopic]);

  const hasUnattemptedDragWords = isDragVocabularyStage && getFirstUnattemptedWordIndex(wordAttempts) !== -1;
  const isCurrentStageComplete = stageInitialized && wordPairs.length > 0 && (currentGameLevel === 4
    ? currentTargetIndex >= wordPairs.length
    : currentTargetIndex >= shuffledWordOrder.length && !hasUnattemptedDragWords);

  useEffect(() => {
    if (!isCurrentStageComplete || showResumeDialog || loadedTopic !== selectedTopic || completedSessionRef.current === stageSession) return;
    completedSessionRef.current = stageSession;
    const progress = completeVocabularyStage(savedProgress, currentGameLevel, allWordPairs, {
      incorrectWords, game3Words, game4Words, game4IncorrectWords
    });
    if (!progress) return;
    setSavedProgress(progress);
    saveVocabularyProgress(selectedGrade, selectedTopicId || selectedTopic, allWordPairs, progress);
  }, [isCurrentStageComplete, showResumeDialog, loadedTopic, selectedTopic, stageSession, savedProgress,
    currentGameLevel, allWordPairs, incorrectWords, game3Words, game4Words, game4IncorrectWords, selectedGrade, selectedTopicId]);

  useEffect(() => {
    if (!stageInitialized || showResumeDialog || (currentGameLevel !== 1 && currentGameLevel !== 3) || currentTargetIndex < shuffledWordOrder.length) return;

    const firstUnattemptedWordIndex = wordPairs.findIndex((_, index) => (wordAttempts[index] || 0) === 0);
    if (firstUnattemptedWordIndex === -1) return;

    const firstUnattemptedPosition = shuffledWordOrder.findIndex(index => index === firstUnattemptedWordIndex);
    setCurrentTargetIndex(firstUnattemptedPosition >= 0 ? firstUnattemptedPosition : 0);
  }, [stageInitialized, showResumeDialog, currentGameLevel, currentTargetIndex, shuffledWordOrder, wordAttempts, wordPairs]); // Guard against skipped drag-game items

  // Calculate final score based on attempts
  const calculateFinalScore = () => {
    let finalScore = 0;
    if (currentGameLevel === 4) {
      // For game 4, count words that were answered correctly on first attempt
      wordPairs.forEach((_, index) => {
        const attempts = game4Attempts[index] || 0;
        if (attempts === 1 && checkedAnswers[index]) {
          finalScore += 1;
        }
      });
    } else {
      wordPairs.forEach((_, index) => {
        const attempts = wordAttempts[index] || 0;
        const wasShownAnswer = wordsShownCorrectAnswer[index];
        // Only count if exactly 1 attempt AND user didn't request to see the answer
        if (attempts === 1 && !wasShownAnswer) {
          finalScore += 1;
        }
      });
    }
    return finalScore;
  };

  // Generate results text for sharing
  const generateResultsText = () => {
    let resultsText = `תוצאות משחק אוצר מילים - משחק ${currentGameLevel}\n`;

    if (currentGameLevel === 4) {
      // No scoring for game 4, just show word pairs and user answers
      resultsText += `סיכום המילים:\n\n`;
      getResultsWordOrder().forEach((index) => {
        const pair = wordPairs[index];
        if (!pair) return;

        const userAnswer = userAnswers[index] || 'לא נענה';
        resultsText += `${pair.arabic} - ${userAnswer}\n\n`;
      });
    } else {
      const finalScore = calculateFinalScore();
      resultsText += `ניקוד: ${finalScore}/${wordPairs.length}\n`;
      resultsText += `סיכום המילים:\n\n`;

      getResultsWordOrder().forEach((index) => {
        const pair = wordPairs[index];
        if (!pair) return;

        resultsText += `${pair.hebrew} - ${pair.arabic}\n`;
        const attempts = wordAttempts[index] || 0;
        if (attempts > 1) {
          resultsText += `*מספר נסיונות: ${attempts}*\n\n`;
        } else {
          resultsText += `מספר נסיונות: ${attempts}\n\n`;
        }
      });
    }

    return resultsText;
  };

  // Generate image from results summary
  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;

    try {
      // Hide buttons before capturing
      const buttonsElement = resultsSummaryRef.current.querySelector('.game-end-buttons');
      if (buttonsElement) {
        buttonsElement.style.display = 'none';
      }

      const canvas = await html2canvas(resultsSummaryRef.current, {
        backgroundColor: '#5f9ea0',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      // Show buttons again after capturing
      if (buttonsElement) {
        buttonsElement.style.display = 'flex';
      }

      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating image:', error);

      // Make sure to show buttons again even if there's an error
      const buttonsElement = resultsSummaryRef.current?.querySelector('.game-end-buttons');
      if (buttonsElement) {
        buttonsElement.style.display = 'flex';
      }

      return null;
    }
  };

  // Convert data URL to blob
  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // State for game 4 incorrect answer popup
  const [showIncorrectPopup, setShowIncorrectPopup] = useState(false);

  const pickStage3Words = () => {
    const shuffledAll = [...allWordPairs].sort(() => Math.random() - 0.5);
    return shuffledAll.slice(0, stage3Size);
  };

  // Handle answer submission for game 4
  const handleAnswerSubmit = () => {
    const raw = currentAnswer.trim();
    if (!raw) return;

    const idx = currentTargetIndex;

    // כבר נענה נכון? אל תשנה תשובה שנשמרה — פשוט התקדם
    if (checkedAnswers[idx]) {
      setCurrentAnswer('');
      const nextIndex = idx + 1;
      setCurrentTargetIndex(nextIndex);
      if (nextIndex < wordPairs.length) {
        setCurrentAnswer(userAnswers[nextIndex] || '');
      }
      if (nextIndex >= wordPairs.length) {
        setShowBalloons(true);
        setTimeout(() => setShowBalloons(false), 4000);
      }
      return;
    }

    // (מכאן זה פריט שעדיין לא נכון)
    const lastChecked = lastCheckedAnswers[idx];
    if (!lastChecked || lastChecked !== raw) {
      setGame4Attempts(prev => ({ ...prev, [idx]: (prev[idx] || 0) + 1 }));
      setLastCheckedAnswers(prev => ({ ...prev, [idx]: raw }));
    }

    const currentWord = wordPairs[idx];
    const acceptableAnswers = [currentWord.hebrew, ...(currentWord.alts || [])]
      .filter(Boolean);

    if (isAcceptedGame4Answer(raw, acceptableAnswers)) {
      // תשובה התקבלה — ננעל ו*נשמור* בדיוק מה שהמשתתף כתב
      setCheckedAnswers(prev => ({ ...prev, [idx]: true }));
      setUserAnswers(prev => ({ ...prev, [idx]: raw }));

      playSuccessSound();
      setShowFireworks(true);
      setTimeout(() => {
        setShowFireworks(false);
        setCurrentAnswer('');
        const nextIndex = idx + 1;
        setCurrentTargetIndex(nextIndex);
        if (nextIndex < wordPairs.length) {
          setCurrentAnswer(userAnswers[nextIndex] || '');
        }
        if (nextIndex >= wordPairs.length) {
          setShowBalloons(true);
          setTimeout(() => setShowBalloons(false), 4000);
        }
      }, 1200);
    } else {
      // תשובה שגויה — אפשר לשמור כדי להציג/לערוך
      playErrorSound();
      setUserAnswers(prev => ({ ...prev, [idx]: raw }));
      // השאר כמו קודם: game4IncorrectWords / game4IncorrectAnswers / popup...
      setGame4IncorrectWords(prev => {
        const exists = prev.some(w => w.arabic === currentWord.arabic && w.hebrew === currentWord.hebrew);
        return exists ? prev : [...prev, currentWord];
      });
      setGame4IncorrectAnswers(prev => ({ ...prev, [idx]: raw }));
      setShowIncorrectPopup(true);
    }
  };

  // Handle incorrect answer popup actions
  const handleTryAgain = () => {
    setShowIncorrectPopup(false);
    // Keep current answer for user to edit
  };

  const handleSkipToNext = () => {
    setShowIncorrectPopup(false);

    // Word was already tracked as incorrect in handleAnswerSubmit
    // Save current answer (already saved in handleAnswerSubmit)
    setCurrentAnswer('');
    const nextIndex = currentTargetIndex + 1;
    setCurrentTargetIndex(nextIndex);

    // Load next answer if exists
    if (nextIndex < wordPairs.length) {
      setCurrentAnswer(userAnswers[nextIndex] || '');
    }

    // Check if this was the last word in game 4
    if (nextIndex >= wordPairs.length) {
      // Show balloons animation
      setShowBalloons(true);

      // Hide balloons after 4 seconds
      setTimeout(() => {
        setShowBalloons(false);
      }, 4000);
    }
  };

  // Navigate to previous word in game 4
  const goToPreviousWord = () => {
    if (currentTargetIndex > 0) {
      // Always save current answer before navigating
      if (currentAnswer.trim()) {
        setUserAnswers(prev => ({
          ...prev,
          [currentTargetIndex]: currentAnswer.trim()
        }));
      }
      const prevIndex = currentTargetIndex - 1;
      setCurrentTargetIndex(prevIndex);
      setCurrentAnswer(userAnswers[prevIndex] || '');
    }
  };

  // Navigate to next word in game 4
  const goToNextWord = () => {
    if (currentTargetIndex < wordPairs.length - 1) {
      // Always save current answer before navigating
      if (currentAnswer.trim()) {
        setUserAnswers(prev => ({
          ...prev,
          [currentTargetIndex]: currentAnswer.trim()
        }));
      }
      const nextIndex = currentTargetIndex + 1;
      setCurrentTargetIndex(nextIndex);
      setCurrentAnswer(userAnswers[nextIndex] || '');
    }
  };

  // Handle email sharing with image
  const shareViaEmail = async () => {
    const imageDataUrl = await generateResultsImage();

    if (imageDataUrl) {
      // Create a download link for the image
      const link = document.createElement('a');
      link.download = `תוצאות-משחק-${currentGameLevel}.png`;
      link.href = imageDataUrl;
      link.click();
    }

    setShowResultsPopup(false);
  };

  // Handle WhatsApp sharing with image
  const shareViaWhatsApp = async () => {
    const imageDataUrl = await generateResultsImage();

    if (imageDataUrl && navigator.share) {
      try {
        // Use Web Share API if available (mobile browsers)
        const blob = dataURLtoBlob(imageDataUrl);
        const file = new File([blob], `תוצאות-משחק-${currentGameLevel}.png`, { type: 'image/png' });

        await navigator.share({
          title: `תוצאות משחק אוצר מילים - משחק ${currentGameLevel}`,
          files: [file]
        });
      } catch (error) {
        console.log('Web Share API failed, downloading image instead');
        // Fallback: download image and open WhatsApp with text
        const link = document.createElement('a');
        link.download = `תוצאות-משחק-${currentGameLevel}.png`;
        link.href = imageDataUrl;
        link.click();

        const resultsText = generateResultsText();
        const text = encodeURIComponent(`${resultsText}\n\nהתמונה הורדה למחשב שלך - אנא צרף אותה לווטסאפ.`);
        const whatsappUrl = `https://wa.me/?text=${text}`;
        window.open(whatsappUrl, '_blank');
      }
    } else if (imageDataUrl) {
      // Fallback: download image and open WhatsApp with text
      const link = document.createElement('a');
      link.download = `תוצאות-משחק-${currentGameLevel}.png`;
      link.href = imageDataUrl;
      link.click();

      const resultsText = generateResultsText();
      const text = encodeURIComponent(`${resultsText}\n\nהתמונה הורדה למחשב שלך - אנא צרף אותה לווטסאפ.`);
      const whatsappUrl = `https://wa.me/?text=${text}`;
      window.open(whatsappUrl, '_blank');
    } else {
      // Final fallback to text only
      const resultsText = generateResultsText();
      const text = encodeURIComponent(resultsText);
      const whatsappUrl = `https://wa.me/?text=${text}`;
      window.open(whatsappUrl, '_blank');
    }

    setShowResultsPopup(false);
  };

  const initializeVocabularyStage = (level) => {
    let currentWordSet;
    if (level === 1) {
      currentWordSet = gameSet1;
    } else if (level === 2) {
      currentWordSet = gameSet2;
    } else if (level === 3) {
      currentWordSet = pickStage3Words();
      setGame3Words(currentWordSet);
    } else if (level === 4) {
      currentWordSet = generateGame4Words();
      setGame4Words(currentWordSet);
    }
    if (!currentWordSet?.length) return;

    setCurrentGameLevel(level);
    setStageSession(session => session + 1);
    completedSessionRef.current = null;
    const newShuffledOrder = Array.from({ length: currentWordSet.length }, (_, i) => i)
      .sort(() => Math.random() - 0.5);
    setShuffledWordOrder(newShuffledOrder);
    setResultsWordOrder(
      level === 4
        ? currentWordSet.map((_, index) => index)
        : level === 2
          ? []
          : newShuffledOrder
    );
    setCurrentTargetIndex(0);
    dragEndHandledRef.current = false;
    dropProcessingRef.current = false;
    activeDragPointerIdRef.current = null;
    resetPointerDrag();
    setShowFireworks(false);
    setShowBalloons(false);
    setShowErrorFlash(false);
    setIncorrectCardId(null);
    setShowIncorrectPopup(false);
    setShowResultsPopup(false);

    // Reset attempts tracking and shown answers
    const initialAttempts = {};
    currentWordSet.forEach((_, index) => {
      initialAttempts[index] = 0;
    });
    wordAttemptsRef.current = initialAttempts;
    setWordAttempts(initialAttempts);
    setWordsShownCorrectAnswer({});

    setCurrentAnswer('');
    setUserAnswers({});
    setCheckedAnswers({});
    setGame4Attempts({});
    setGame4IncorrectAnswers({});
    setLastCheckedAnswers({});
    setGame4IncorrectWords([]);
    setIncorrectWords([]);

    const resetCards = currentWordSet.map((pair, index) => ({
      id: index + 1,
      word: level === 3 ? pair.arabic : pair.hebrew,
      translation: level === 3 ? pair.hebrew : pair.arabic,
      revealed: true,
      matched: false
    }));
    const shuffled = [...resetCards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setStageInitialized(true);
    setShowResumeDialog(false);
  };

  const startVocabularyStage = (level) => {
    if (level < 1 || level > getUnlockedVocabularyStage(savedProgress)) return;
    initializeVocabularyStage(level);
  };

  const startNewGame = () => initializeVocabularyStage(currentGameLevel);

  // Handle grade selection
  const navigate = useNavigate();
  const getVocabBasePath = () => {
    if (gradeFromParams) {
      return `/grade/${gradeFromParams}/vocabulary`;
    }
    if (gradeFromUrl === 'grade7' && !gradeFromParams) {
      return '/junior-high/vocabulary';
    }
    if (actualGrade) {
      return `/grade/${actualGrade}/vocabulary`;
    }
    return '/';
  };
  
  const handleGradeSelect = (grade, gradeDisplayName) => {
    setSelectedGrade(grade);
    setGradeName(gradeDisplayName);
    setSelectedTopic(null);
    setSelectedTopicId(null);
    setTopicName('');
    navigate(`/${grade}`);
  };

  // Handle topic selection
  const applyTopicSelection = (topic, { shouldNavigate = true } = {}) => {
    if (!topic) return;
    setStageInitialized(false);
    setLoadedTopic(null);
    setSavedProgress(null);
    setShowResumeDialog(false);
    setAllWordPairs([]);
    setSelectedTopic(topic.file);
    setSelectedTopicId(topic.id);
    setTopicName(topic.name);
    setCurrentGameLevel(1);
    setCurrentTargetIndex(0);
    setResultsWordOrder([]);
    dragEndHandledRef.current = false;
    dropProcessingRef.current = false;
    setIncorrectWords([]);
    setGame4IncorrectWords([]);
    if (shouldNavigate) {
      const basePath = getVocabBasePath();
      navigate(`${basePath}/${topic.id}`);
    }
  };

  const handleTopicSelect = (topic) => {
    applyTopicSelection(topic);
  };

  // Handle back to grade selection
  const handleBackToGradeSelection = () => {
    // Use window.location to force a full page reload to home
    window.location.href = '/';
  };

  // Handle back to topic selection (for vocabulary games)
  const handleBackToTopicSelection = () => {
    setStageInitialized(false);
    setLoadedTopic(null);
    setSavedProgress(null);
    setShowResumeDialog(false);
    setAllWordPairs([]);
    setSelectedTopic(null);
    setSelectedTopicId(null);
    setTopicName('');
    setCurrentGameLevel(1);
    setCurrentTargetIndex(0);
    setResultsWordOrder([]);
    dragEndHandledRef.current = false;
    dropProcessingRef.current = false;
    setIncorrectWords([]);
    setGame4IncorrectWords([]);
    navigate(getVocabBasePath());
  };

  // Set grade from URL parameter
  useEffect(() => {
    if (actualGrade && !selectedGrade && actualGrade !== 'adminsky') {
      setSelectedGrade(actualGrade);
      const gradeNames = {
        'grade7': 'Grade 7',
        'grade8': 'Grade 8',
        'grade9': 'Grade 9',
        'grade10': 'Grade 10',
        'grade11': 'Grade 11',
        'grade12': 'Grade 12'
      };
      setGradeName(gradeNames[actualGrade] || '');
    }
  }, [actualGrade, selectedGrade]);

  useEffect(() => {
    if (!topicIdFromParams) {
      setSelectedTopic(null);
      setSelectedTopicId(null);
      setTopicName('');
      return;
    }

    if (!selectedGrade) return;

    if (selectedTopicId === topicIdFromParams && selectedTopic) {
      return;
    }

    const matchedTopic = findTopicById(selectedGrade, topicIdFromParams);
    if (matchedTopic) {
      applyTopicSelection(matchedTopic, { shouldNavigate: false });
    } else {
      setSelectedTopic(null);
      setSelectedTopicId(null);
      setTopicName('');
    }
  }, [topicIdFromParams, selectedGrade, selectedTopic, selectedTopicId]);

  // DEBUG: Add debug information
  console.log('DEBUG - allWordPairs.length:', allWordPairs.length);
  console.log('DEBUG - wordPairs.length:', wordPairs.length);
  console.log('DEBUG - currentTargetIndex:', currentTargetIndex);
  console.log('DEBUG - shuffledWordOrder.length:', shuffledWordOrder.length);
  console.log('DEBUG - cards.length:', cards.length);
  console.log('DEBUG - currentGameLevel:', currentGameLevel);

  // Show grade selector if no grade is selected or if adminsky route
  if (!selectedGrade || actualGrade === 'adminsky') {
    return <GradeSelector onGradeSelect={handleGradeSelect} isAdminsky={actualGrade === 'adminsky'} />;
  }

  // Show topic selector if grade is selected but no topic is selected
  if (!selectedTopic) {
    if (topicIdFromParams) {
      return (
        <div className="App">
          <header className="App-header">
            <h1>טוען משחק</h1>
            <p>רגע, זה נטען...</p>
          </header>
        </div>
      );
    }
    return <TopicSelector 
          onTopicSelect={handleTopicSelect}
          onBackToGradeSelection={handleBackToGradeSelection}
          selectedGrade={selectedGrade}
        />;
  }

  // Show loading message while CSV data is being loaded
  if (allWordPairs.length === 0 || loadedTopic !== selectedTopic) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>אוצר מילים - {topicName}</h1>
          <p>טוען מילים...</p>
        </header>
      </div>
    );
  }

  if (showResumeDialog && savedProgress) {
    return (
      <div className="App vocab-game">
        <VocabularyResumeDialog topicName={topicName} progress={savedProgress} onSelectStage={startVocabularyStage} onBack={handleBackToTopicSelection} />
      </div>
    );
  }
  if (!stageInitialized) return <div className="App loading">טוען משחק...</div>;
  const orderedResultIndexes = getResultsWordOrder();

  return (
    <div className={`App vocab-game vocab-game-level-${currentGameLevel}`}>
      <header className="App-header">
        <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
          <button onClick={handleBackToTopicSelection} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
            חזרה למשחקי אוצר מילים ↩
          </button>
          <h1 className="game-title-with-back">
            {isCurrentStageComplete ? (
              'תוצאות המשחק'
            ) : (
              <>
                אוצר מילים - {topicName}
                <span className="progress-header-stage">
                  {' '}
                  {currentGameLevel}/4
                </span>
              </>
            )}
          </h1>
        </div>
        {!isCurrentStageComplete && (
          <p className="game-instructions-animate">
            {currentGameLevel === 4
              ? 'כתוב את התרגום של המילה הערבית'
              : currentGameLevel === 3 
              ? 'גרור את הקלף המתאים למרכז המסגרת המקווקוות'
              : currentGameLevel === 2
              ? 'השתמש בחצים כדי לעבור בין האפשרויות ובחר את התשובה הנכונה'
              : 'גרור את הקלף המתאים למרכז המסגרת המקווקוות '
            }
          </p>
        )}
        {isCurrentStageComplete && (
          <>
            <br/>
            <br/>
          </>
        )}
      </header>
      <main className={`game-container ${isCurrentStageComplete ? 'results-screen' : ''} ${currentGameLevel === 4 ? 'game4-mode' : ''} ${currentGameLevel === 3 ? 'game3-mode' : ''} ${currentGameLevel === 2 ? 'game2-mode' : ''}`}>
        {currentGameLevel === 2 && currentTargetIndex < shuffledWordOrder.length ? (
          <VocabGame2
            key={`${selectedTopic}:${stageSession}`}
            wordPairs={gameSet2}
            onComplete={() => {
              setCurrentTargetIndex(shuffledWordOrder.length);
            }}
            onWordOrderGenerated={setResultsWordOrder}
            onWordAttempt={(wordIndex, isCorrect, attempts) => {
              setWordAttempts(prev => {
                const nextAttempts = {
                  ...prev,
                  [wordIndex]: attempts
                };
                wordAttemptsRef.current = nextAttempts;
                return nextAttempts;
              });
              // If user requested to see correct answer (isCorrect = false), mark it
              if (!isCorrect) {
                setWordsShownCorrectAnswer(prev => ({
                  ...prev,
                  [wordIndex]: true
                }));
              }
              // Track incorrect word for game 4 (if more than 1 attempt or if answer was shown)
              if (attempts > 1 || !isCorrect) {
                const incorrectWord = gameSet2[wordIndex];
                // Only add if not already in the list
                setIncorrectWords(prev => {
                  const isDuplicate = prev.some(word => 
                    word.arabic === incorrectWord.arabic && word.hebrew === incorrectWord.hebrew
                  );
                  if (!isDuplicate) {
                    console.log('DEBUG - Added incorrect word from game 2:', incorrectWord);
                    return [...prev, incorrectWord];
                  }
                  return prev;
                });
              }
            }}
            normalizeHebrew={normalizeHebrew}
          />
        ) : currentGameLevel === 4 ? (
          <div className="game4-container">
            <div className="instruction-box">
              {isCurrentStageComplete ? (
                <p className="congratulations-message">כל הכבוד, סיימת!</p>
              ) : (
                <div className="instruction-text">
                  <p className="arabic-word vocab-arabic-word" lang="ar" dir="rtl">
                    {wordPairs[currentTargetIndex]?.arabic}
                  </p>
                </div>
              )}
            </div>
            {currentTargetIndex < wordPairs.length && (
              <div className="game4-input-section">
                {(() => {
                  const isLocked = !!checkedAnswers[currentTargetIndex];
                  const game4InputPlaceholderText = isLocked ? "התשובה ננעלה לאחר בדיקה" : "הקלד את התשובה בעברית";
                  const shouldShowGame4Placeholder = !currentAnswer && !isGame4InputFocused;
                  return (
                    <>
                      {!isLocked && <div className="pencil-icon"></div>}
                      {isLocked && (
                        <div className="check-badge" aria-label="נכון">✓</div>
                      )}
                      <div className="game4-input-wrapper">
                        <input
                          type="text"
                          value={currentAnswer}
                          onChange={(e) => { if (!isLocked) setCurrentAnswer(e.target.value); }}
                          aria-label={game4InputPlaceholderText}
                          className={`game4-input ${isLocked ? 'locked' : ''}`}
                          readOnly={isLocked}
                          onFocus={() => setIsGame4InputFocused(true)}
                          onBlur={() => setIsGame4InputFocused(false)}
                          onKeyDown={(e) => e.key === 'Enter' && !isLocked && handleAnswerSubmit()}
                        />
                        {shouldShowGame4Placeholder && (
                          <span className="game4-input-placeholder">
                            {game4InputPlaceholderText}
                          </span>
                        )}
                      </div>
                    </>
                  );
                })()}
                <div className="progress-indicator">
                  {currentTargetIndex + 1}/{wordPairs.length}
                </div>
                <div className="game4-navigation">
                  <button 
                    onClick={goToPreviousWord} 
                    disabled={currentTargetIndex === 0}
                    className="nav-button prev-button"
                  >
                    <div className="nav-button-icon back-icon"></div>
                    חזור
                  </button>
                  <button 
                    onClick={handleAnswerSubmit} 
                    className="nav-button next-button"
                  >
                    בדוק והמשך
                    <div className="nav-button-icon next-icon"></div>
                  </button>
                </div>
                {showFireworks && (
                  <div className="fireworks-container">
                    <div className="firework firework-1"></div>
                    <div className="firework firework-2"></div>
                    <div className="firework firework-3"></div>
                    <div className="firework firework-4"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div 
            className={`instruction-box ${showFireworks ? 'celebrating' : ''} ${isCurrentStageComplete ? 'game-complete' : ''}`}
          >
            {isCurrentStageComplete ? (
              <p className="congratulations-message">כל הכבוד, סיימת!</p>
            ) : (
              <div className="instruction-text">
                <p
                  className={`${currentGameLevel === 3 ? 'hebrew-target-word' : 'arabic-word vocab-arabic-word'} ${showErrorFlash ? 'error-flash' : ''}`}
                  lang={currentGameLevel === 3 ? 'he' : 'ar'}
                  dir="rtl"
                >
                  {currentGameLevel === 3 
                    ? wordPairs[shuffledWordOrder[currentTargetIndex]]?.hebrew
                    : wordPairs[shuffledWordOrder[currentTargetIndex]]?.arabic
                  }
                </p>
              </div>
            )}
            {showFireworks && (
              <div className="fireworks-container">
                <div className="firework firework-1"></div>
                <div className="firework firework-2"></div>
                <div className="firework firework-3"></div>
                <div className="firework firework-4"></div>
              </div>
            )}
          </div>
        )}
        {currentGameLevel !== 4 && currentGameLevel !== 2 && currentTargetIndex < shuffledWordOrder.length && (
          <div
            className={`cards-grid ${wordPairs.length === 9 ? 'cards-grid-nine' : ''}`}
            onPointerMove={handlePointerMove}
          >
            {cards.map(card => (
              <div 
                key={card.id}
                className={`card revealed ${incorrectCardId === card.id ? 'incorrect-return' : ''} ${card.matched ? 'matched-hidden' : ''} ${draggedCard?.id === card.id ? 'dragging' : ''}`}
                onPointerDown={(e) => !card.matched && handlePointerStart(e, card)}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerCancel}
                onLostPointerCapture={handlePointerCancel}
                style={{ touchAction: 'none' }}
              >
                <div className="card-inner">
                  <div className="card-front">?</div>
                  <div className="card-back" lang={currentGameLevel === 3 ? 'ar' : 'he'} dir="rtl">{card.word}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {isCurrentStageComplete && (
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>אוצר מילים - {topicName}</h3>
            <div className="final-score-display">
              ניקוד: {calculateFinalScore()}/{wordPairs.length}
            </div>
            <br/>
            <div className="words-list">
              {orderedResultIndexes.map((index) => {
                const pair = wordPairs[index];
                if (!pair) return null;

                return (
                  <div key={index} className="word-item">
                    <div className="word-pair">
                      {currentGameLevel !== 3 ? (
                        <>
                          <span className="arabic-word-summary">{pair.arabic}</span>
                          <span className="separator"> - </span>
                          <span className="hebrew-word">
                            {pair.hebrew}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="hebrew-word">{pair.hebrew}</span>
                          <span className="separator"> - </span>
                          <span className="arabic-word-summary">{pair.arabic}</span>
                        </>
                      )}
                    </div>
                    {currentGameLevel === 4 ? (
                      <div className={`attempts-info ${game4IncorrectAnswers[index] ? 'multiple-attempts' : ''}`}>
                        מספר נסיונות: {game4Attempts[index] || 0}
                        {game4IncorrectAnswers[index] && (
                          <span> תשובתך: {game4IncorrectAnswers[index]}</span>
                        )}
                      </div>
                    ) : (
                      <div className={`attempts-info ${(wordAttempts[index] || 0) > 1 || wordsShownCorrectAnswer[index] ? 'multiple-attempts' : ''}`}>
                        מספר נסיונות: {wordAttempts[index] || 0}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="game-end-buttons">
              <button onClick={startNewGame} className="game-button restart-button">
                שחק מחדש
              </button>
              <button 
                onClick={() => startVocabularyStage(currentGameLevel + 1)}
                className="game-button next-button"
                disabled={currentGameLevel === 4}
              >
                {currentGameLevel < 4 ? 'התקדם לשלב הבא' : 'סיימת את כל השלבים'}
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>
        )}

        {showResultsPopup && (
          <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
              <h3>שתף תוצאות</h3>
              <p>בחר איך ברצונך לשתף את התוצאות:</p>
              <div className="popup-buttons">
                <button onClick={shareViaEmail} className="popup-button email-button">
                  💾 הורד תמונה
                </button>
                <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                  💬 ווטסאפ
                </button>
              </div>
              <button onClick={() => setShowResultsPopup(false)} className="popup-close-button">
                ✕
              </button>
            </div>
          </div>
        )}

        {showIncorrectPopup && (
          <div className="popup-overlay">
            <div className="popup-content incorrect-popup" onClick={(e) => e.stopPropagation()}>
              <h3>תשובה שגויה</h3>
              <p>התשובה שהזנת אינה נכונה.</p>
              <p>האם ברצונך לנסות שוב או לעבור למילה הבאה?</p>
              <div className="popup-buttons">
                <button onClick={handleTryAgain} className="popup-button try-again-button">
                  נסה שוב
                </button>
                <button onClick={handleSkipToNext} className="popup-button skip-button">
                  עבור למילה הבאה
                </button>
              </div>
            </div>
          </div>
        )}

        {showBalloons && (
          <div className="balloons-container">
            <div className="balloon balloon-1"></div>
            <div className="balloon balloon-2"></div>
            <div className="balloon balloon-3"></div>
          </div>
        )}
      </main>
    </div>
  );
}

// Component for handling grade-specific routes
function GradeRoute() {
  const { grade } = useParams();
  return <GameApp gradeFromUrl={grade} />;
}

// Component wrapper for verb game
function VerbGameRoute() {
  const { grade, gameType } = useParams();
  
  // משחק "מהי הצורה הדקדוקית?"
  if (gameType === 'grammatical-form') {
    return <VerbTenseGame />;
  }
  
  if (gameType === 'grammatical-form-binyan2') {
    return <VerbTenseGame gameConfig={{ binyan: '2' }} />;
  }
  
  if (gameType === 'grammatical-form-binyan3') {
    return <VerbTenseGame gameConfig={{ binyan: '3' }} />;
  }

  if (gameType === 'grammatical-form-binyan4') {
    return <VerbTenseGame gameConfig={{ binyan: '4' }} />;
  }

  if (gameType === 'grammatical-form-binyan5') {
    return <VerbTenseGame gameConfig={{ binyan: '5' }} />;
  }

  if (gameType === 'grammatical-form-binyan2-all-three') {
    return <VerbTenseGame gameConfig={{ binyan: '2', withImperative: true }} />;
  }

  if (gameType === 'grammatical-form-binyan3-all-three') {
    return <VerbTenseGame gameConfig={{ binyan: '3', withImperative: true }} />;
  }

  if (gameType === 'grammatical-form-binyan4-all-three') {
    return <VerbTenseGame gameConfig={{ binyan: '4', withImperative: true }} />;
  }
  
  // משחק "מהי הצורה הדקדוקית?" מורחב עם בינוני
  if (gameType === 'grammatical-form-extended') {
    return <VerbTenseGame2 />;
  }
  if (gameType === 'grammatical-form-binyan2-extended') {
    return <VerbTenseGame2 gameConfig={{ binyan: '2' }} />;
  }
  if (gameType === 'grammatical-form-binyan2-with-masdar') {
    return <VerbTenseGame2 gameConfig={{
      binyan: '2',
      includeMasdar: true,
      shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-2-כולל-מצדר.png'
    }} />;
  }
  if (gameType === 'grammatical-form-binyan3-extended') {
    return <VerbTenseGame2 gameConfig={{ binyan: '3' }} />;
  }
  if (gameType === 'grammatical-form-binyan3-with-masdar') {
    return <VerbTenseGame2 gameConfig={{
      binyan: '3',
      includeMasdar: true,
      shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-3-כולל-מצדר.png'
    }} />;
  }
  if (gameType === 'grammatical-form-binyan4-extended') {
    return <VerbTenseGame2 gameConfig={{ binyan: '4' }} />;
  }
  if (gameType === 'grammatical-form-binyan4-with-masdar') {
    return <VerbTenseGame2 gameConfig={{
      binyan: '4',
      includeMasdar: true,
      shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-4-כולל-מצדר.png'
    }} />;
  }
  
  // משחק "מהי הצורה הדקדוקית?" עם מצדר
  if (gameType === 'grammatical-form-with-masdar') {
    return <VerbTenseGame3 />;
  }
  
  // משחק בינוני פועל ופעול
  if (gameType === 'participles-binyan1') {
    return <ParticiplesGame />;
  }
  if (gameType === 'participles-binyan2') {
    return <ParticiplesGameII />;
  }
  if (gameType === 'participles-binyan3') {
    return <ParticiplesGameIII />;
  }
  if (gameType === 'participles-binyan4') {
    return <ParticiplesGame gameConfig={PARTICIPLES_GAME_CONFIGS.binyan4} />;
  }
  
  // משחק צורת הציווי
  if (gameType === 'imperative-matching-binyan1') {
    return <ImperativeMatchingGame />;
  }
  if (gameType === 'binyan2-imperative') {
    return <ImperativeMatchingGameII />;
  }
  if (gameType === 'binyan3-imperative') {
    return <ImperativeMatchingGameIII />;
  }
  if (gameType === 'binyan4-imperative') {
    return <ImperativeMatchingGameIII gameConfig={{ binyan: '4' }} />;
  }
  
  // משחק מהו הזמן? - עבר, הווה-עתיד וציווי
  if (gameType === 'verb-tense-game') {
    return <VerbTenseGame1 />;
  }
  
  // קונפיגורציות למשחקי ניתוח פועל
  if (gameType === 'verb-analysis-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - עבר',
      allowedBinyans: null, // כל הבניינים
      allowedTenses: ['עבר']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan1-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 1 עבר',
      allowedBinyans: ['1'],
      allowedTenses: ['עבר']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan1-both') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 1 עבר והווה-עתיד',
      allowedBinyans: ['1'],
      allowedTenses: ['עבר', 'הווה-עתיד']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan1-all-three') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 1 עבר, הווה-עתיד וציווי',
      allowedBinyans: ['1'],
      allowedTenses: ['עבר', 'הווה-עתיד', 'ציווי']
    }} />;
  }
  
  if (gameType === 'verb-analysis-extended') {
    return <VerbAnalysisGameExtended />;
  }
  if (gameType === 'verb-analysis-binyan2-extended') {
    return <VerbAnalysisGameExtended gameConfig={{ binyan: '2' }} />;
  }
  if (gameType === 'verb-analysis-binyan3-extended') {
    return <VerbAnalysisGameExtended gameConfig={{ binyan: '3' }} />;
  }
  if (gameType === 'verb-analysis-binyan4-extended') {
    return <VerbAnalysisGameExtended gameConfig={{ binyan: '4' }} />;
  }

  if (gameType === 'masdar-game-binyan3') {
    return <MasdarBalloonGameBinyan4 gameConfig={{ binyan: '3' }} />;
  }

  if (gameType === 'masdar-balloon-game-binyan4') {
    return <MasdarBalloonGameBinyan4 />;
  }

  if (gameType === 'masdar-game-binyan2') {
    return <MasdarBalloonGameBinyan4 gameConfig={{ binyan: '2' }} />;
  }
  
  if (gameType === 'verb-analysis-complete') {
    return <VerbAnalysisGameComplete key="verb-analysis-complete" />;
  }

  if (gameType === 'verb-analysis-binyan2-complete') {
    return <VerbAnalysisGameComplete key="verb-analysis-binyan2-complete" gameConfig={{ binyan: '2' }} />;
  }

  if (gameType === 'verb-analysis-binyan3-complete') {
    return <VerbAnalysisGameComplete key="verb-analysis-binyan3-complete" gameConfig={{ binyan: '3' }} />;
  }
  
  if (gameType === 'verb-analysis-binyan4-complete') {
    return <VerbAnalysisGameComplete key="verb-analysis-binyan4-complete" gameConfig={{ binyan: '4' }} />;
  }
  
  if (gameType === 'verb-analysis-binyan1-present') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 1 הווה-עתיד',
      allowedBinyans: ['1'],
      allowedTenses: ['הווה-עתיד']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan2-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 2 עבר',
      allowedBinyans: ['1', '2'],
      allowedTenses: ['עבר'],
      binyanDistribution: { '2': 6, '1': 2 }
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan2-present') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 2 הווה-עתיד',
      allowedBinyans: ['1', '2'],
      allowedTenses: ['הווה-עתיד'],
      binyanDistribution: { '2': 6, '1': 2 }
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan2-mixed') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 2 עבר והווה-עתיד',
      allowedBinyans: ['1', '2'],
      allowedTenses: ['עבר', 'הווה-עתיד'],
      tensePerBinyanDistribution: { '2': { 'עבר': 3, 'הווה-עתיד': 3 }, '1': { 'עבר': 1, 'הווה-עתיד': 1 } }
    }} />;
  }

  if (gameType === 'verb-analysis-binyan2-all-three') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 2 עבר, הווה-עתיד וציווי',
      allowedBinyans: ['1', '2'],
      allowedTenses: ['עבר', 'הווה-עתיד', 'ציווי'],
      binyan2AllThreeConfig: true
    }} />;
  }

  if (gameType === 'verb-analysis-binyan3-mixed') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 3 עבר והווה-עתיד',
      allowedBinyans: ['1', '2', '3'],
      allowedTenses: ['עבר', 'הווה-עתיד'],
      binyan3MixedConfig: true,
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '3', secondaryBinyans: ['1', '2'] }
    }} />;
  }

  if (gameType === 'verb-analysis-binyan3-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 3 עבר',
      allowedBinyans: ['1', '2', '3'],
      allowedTenses: ['עבר'],
      binyanDistribution: { '3': 5, '2': 2, '1': 1 },
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '3', secondaryBinyans: ['1', '2'] }
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan3-present') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 3 הווה-עתיד',
      allowedBinyans: ['1', '2', '3'],
      allowedTenses: ['הווה-עתיד'],
      binyanDistribution: { '3': 5, '2': 2, '1': 1 },
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '3', secondaryBinyans: ['1', '2'] }
    }} />;
  }

  if (gameType === 'verb-analysis-binyan4-present') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 4 הווה-עתיד',
      allowedBinyans: ['1', '2', '3', '4'],
      allowedTenses: ['הווה-עתיד'],
      binyanDistribution: { '4': 5, '1': 1, '2': 1, '3': 1 },
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '4', secondaryBinyans: ['1', '2', '3'] }
    }} />;
  }

  if (gameType === 'verb-analysis-binyan5-present') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 5 הווה-עתיד',
      allowedBinyans: ['2', '5'],
      allowedTenses: ['הווה-עתיד'],
      allowedGizraPatternsByBinyan: {
        '5': ['שלמים', 'כפולים', 'ע"ע', 'פ"ו', 'פ"י', 'ע"ו', 'ע"י']
      },
      binyanDistribution: { '5': 5, '2': 3 },
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '5', secondaryBinyans: ['2'] },
      binyan5PresentConfig: true
    }} />;
  }

  if (gameType === 'verb-analysis-binyan3-all-three') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 3 עבר, הווה-עתיד וציווי',
      allowedBinyans: ['1', '2', '3'],
      allowedTenses: ['עבר', 'הווה-עתיד', 'ציווי'],
      binyan3AllThreeConfig: true,
      analysisMixingPolicy: { type: 'primaryHeavy', primaryBinyan: '3', secondaryBinyans: ['1', '2'] }
    }} />;
  }

  if (gameType === 'verb-analysis-binyan4-all-three') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 4 עבר, הווה-עתיד וציווי',
      allowedBinyans: ['1', '2', '3', '4'],
      allowedTenses: ['עבר', 'הווה-עתיד', 'ציווי'],
      binyan4AllThreeConfig: true,
      analysisMixingPolicy: { type: 'primaryHeavy', primaryBinyan: '4', secondaryBinyans: ['1', '2', '3'] }
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan4-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 4 עבר',
      allowedBinyans: ['1', '2', '3', '4'],
      allowedTenses: ['עבר'],
      binyanDistribution: { '4': 5, '1': 1, '2': 1, '3': 1 },
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '4', secondaryBinyans: ['1', '2', '3'] }
    }} />;
  }

  if (gameType === 'verb-analysis-binyan4-mixed') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 4 עבר והווה-עתיד',
      allowedBinyans: ['1', '2', '3', '4'],
      allowedTenses: ['עבר', 'הווה-עתיד'],
      binyan4MixedConfig: true,
      analysisMixingPolicy: { type: 'fivePlusThree', primaryBinyan: '4', secondaryBinyans: ['1', '2', '3'] }
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan5-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 5 עבר',
      allowedBinyans: ['5'],
      allowedTenses: ['עבר']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan6-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 6 עבר',
      allowedBinyans: ['6'],
      allowedTenses: ['עבר']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan7-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 7 עבר',
      allowedBinyans: ['7'],
      allowedTenses: ['עבר']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan8-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 8 עבר',
      allowedBinyans: ['8'],
      allowedTenses: ['עבر']
    }} />;
  }
  
  if (gameType === 'verb-analysis-binyan10-past') {
    return <VerbAnalysisGame gameConfig={{
      title: 'ניתוח פועל - בניין 10 עבר',
      allowedBinyans: ['10'],
      allowedTenses: ['עבר']
    }} />;
  }
  
  if (gameType === 'masdar-game') {
    return <MasdarBalloonGameBinyan4 gameConfig={{ binyan: '1' }} />;
  }

  return <VerbGameWhoDidIt gameType={gameType} />;
}

// Main App component with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<GradeSelector onGradeSelect={(grade, name) => {}} />} />
        <Route path="/junior-high" element={<JuniorHighSubjects />} />
        <Route path="/junior-high/spoken" element={<JuniorHighSpoken />} />
        <Route path="/junior-high/verbs" element={<JuniorHighVerbs />} />
        <Route path="/junior-high/grammar" element={<JuniorHighGrammarGames />} />
        <Route path="/junior-high/grammar/:gameId" element={<JuniorHighGrammarGameRoute />} />
        <Route path="/junior-high/:subject/grammar" element={<JuniorHighGrammarGames />} />
        <Route path="/junior-high/:subject/grammar/:gameId" element={<JuniorHighGrammarGameRoute />} />
        <Route path="/junior-high/vocabulary" element={<GameApp gradeFromUrl="grade7" />} />
        <Route path="/junior-high/vocabulary/:topicId" element={<GameApp gradeFromUrl="grade7" />} />
        <Route path="/adminsky" element={<GameApp gradeFromUrl="adminsky" />} />
        <Route path="/grade/:grade" element={<SubjectSelector />} />
        <Route path="/grade/:grade/vocabulary" element={<GameApp />} />
        <Route path="/grade/:grade/vocabulary/:topicId" element={<GameApp />} />
        <Route path="/grade/:grade/verbs" element={<VerbsRouter />} />
        <Route path="/grade/:grade/verbs/binyan/:binyan" element={<VerbGameSelector />} />
        <Route path="/grade/:grade/verbs/:gameType" element={<VerbGameRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
